<template>
  <div
    class="fill-height pa-1 lighten-5"
    :class="realizationColor"
  >
    <div
      v-if="!pivotData"
      class="text-center grey--text"
    >
      N/A
    </div>
    <div
      v-else
      class="clickable"
      @click="modalVisible = true"
    >
      <div
        class="text-no-wrap"
        style="opacity: .4"
      >
        Matched across
      </div>
      <div
        v-if="pivotData.market_data"
        class="text-no-wrap"
      >
        {{ pivotData.market_data.companies_count || 0 }} companies / {{ pivotData.market_data.yachts_count || 0 }} yachts
      </div>
      <div
        v-else
        class="text-no-wrap"
      >
        no data
      </div>
      <div
        class="text-no-wrap mt-1"
        style="opacity: .4"
      >
        Market Daily Price
      </div>
      <div class="text-no-wrap">
        {{ pivotData.market_available_daily_price | currency('EUR', 2) }}
      </div>
      <div
        class="text-no-wrap mt-1"
        style="opacity: .4"
      >
        Market Booked
      </div>
      <div class="text-no-wrap">
        {{ pivotData.market_booked_days }} / {{ pivotData.market_available_days }} ({{ pivotData.market_booked_days / pivotData.market_available_days | percentage }}) at {{ pivotData.market_booked_daily_price | currency('EUR', 2) }}
      </div>
      <div
        class="mt-1 text-no-wrap"
        style="opacity: .4"
      >
        Navigare Daily Price
      </div>
      <div class="text-no-wrap">
        {{ pivotData.daily_price | currency('EUR', 2) }}
      </div>
      <div
        class="mt-1 text-no-wrap"
        style="opacity: .4"
      >
        Navigare Booked
      </div>
      <div class="text-no-wrap">
        {{ pivotData.booked_days || 0 }} ({{ pivotData.booked_days / pivotData.available_days | percentage }}) at {{ pivotData.booked_daily_price || 0 | currency('EUR', 2) }} ({{ pivotData.booked_daily_price / (pivotData.daily_price || 1) | percentage }})
      </div>
      <div
        class="mt-1 text-no-wrap"
        style="opacity: .4"
      >
        Navigare Total Income
      </div>
      <div
        class="text-no-wrap"
        :class="realizationColor + '--text'"
      >
        {{ pivotData.booked_total_price | currency('EUR', 2) }} ({{ pivotData.booked_total_price / (pivotData.available_days * pivotData.daily_price) | percentage }})
      </div>
    </div>
    <content-dialog
      small-title
      max-width="600px"
      :visible.sync="modalVisible"
    >
      <template v-slot:dialogTitle>
        Market Data
      </template>

      <v-card-text v-if="pivotData && pivotData.market_data && pivotData.market_data.companies">
        <v-card
          outlined
          class="mb-4"
        >
          <v-card-text>
            Matched across <strong>{{ pivotData.market_data.companies_count }}</strong> companies and <strong>{{ pivotData.market_data.yachts_count }}</strong> yachts.
          </v-card-text>
        </v-card>

        <v-text-field
          v-model="filterCompanies"
          outlined
          label="Search companies"
        />

        <v-card
          v-for="company in filteredCompanies"
          :key="company.id"
          outlined
          class="mb-1"
          background-color="warning"
        >
          <v-card-title
            class="body-2 px-2 py-1 no-gutters"
          >
            <v-col>
              {{ company.name }}
            </v-col>
            <v-col
              cols="auto"
              class="pl-3"
            >
              EUR {{ company.daily_client_price }}
            </v-col>
            <v-col
              cols="auto"
              class="pl-3"
              :class="{ 'red--text': company.booked_days_count >= company.available_days_count, 'warning--text': company.booked_days_count > 0 && company.booked_days_count < company.available_days_count, 'grey--text': company.booked_days_count === 0 }"
            >
              {{ company.booked_days_count }} / {{ company.available_days_count }} days ({{ company.booked_days_count / company.available_days_count | percentage }})
            </v-col>
          </v-card-title>

          <v-card-text class="px-2">
            <v-row
              v-for="yacht in company.yachts"
              :key="yacht.id"
              no-gutters
              class="px-2 py-1 list--bordered"
              :class="{ 'red--text': yacht.booked_days_count >= yacht.available_days_count, 'warning--text': yacht.booked_days_count > 0 && yacht.booked_days_count < yacht.available_days_count, 'grey--text text--darken-2': yacht.booked_days_count === 0 }"
            >
              <v-col>
                <div>{{ yacht.name }}</div>
                <div class="caption">
                  {{ yacht.model_name }}
                </div>
              </v-col>
              <v-col
                cols="auto"
                class="caption"
              >
                <div>EUR {{ yacht.daily_client_price }}</div>
                <div :class="{ 'red--text': yacht.booked_days_count >= yacht.available_days_count, 'warning--text': yacht.booked_days_count > 0 && yacht.booked_days_count < yacht.available_days_count, 'grey--text': yacht.booked_days_count === 0 }">
                  {{ yacht.booked_days_count }} / {{ yacht.available_days_count }} ({{ yacht.booked_days_count / yacht.available_days_count | percentage }})
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-card-text>
    </content-dialog>
  </div>
</template>

<script>
import CurrencyInput from '@/components/helpers/CurrencyInput'
import ContentDialog from '@/components/helpers/Dialog.vue'

export default {
    name: 'YachtModelBookingPricelistTableColumn',
    components: {
      ContentDialog,
      // eslint-disable-next-line vue/no-unused-components
      CurrencyInput
    },
    props: {
      pivotData: {
        type: Object,
        default: null
      },
      pivotDataDirty: {
        type: Object,
        default: null
      }
    },
    data() {
      return {
        data: {
          currency: null,
          weekly_price: null,
          predicted_weekly_occupancy: null
        },
        modalVisible: false,
        filterCompanies: null
      }
    },
    computed: {
      isDirty() {
        for (let key of Object.keys(this.data)) {
          if (this.data[key] !== this.pivotData[key]) return true
        }
        return false
      },
      totalRealization() {
        if (!this.pivotData) return 0

        return this.pivotData.booked_total_price / (this.pivotData.available_days * this.pivotData.daily_price)
      },
      realizationColor() {
        if (this.totalRealization <= 0.3) return 'red'
        if (this.totalRealization <= 0.6) return 'orange'
        if (this.totalRealization <= 0.75) return 'blue'

        return 'green'
      },
      filteredCompanies() {
        if (!this.filterCompanies) return this.pivotData.market_data.companies

        return this.pivotData.market_data.companies.filter(c => {
          return c.name.toLowerCase().includes(this.filterCompanies.toLowerCase())
        })
      }
    },
    watch: {
      pivotDataDirty(pivotDataDirty) {
        if (!pivotDataDirty) return

        this.copyPivotDataDirty(pivotDataDirty)
      }
    },
    mounted() {
      if (this.pivotData && this.pivotDataDirty) this.copyPivotDataDirty(this.pivotDataDirty)
    },
    methods: {
      copyPivotDataDirty(pivotDataDirty) {
        this.data.currency = pivotDataDirty.currency
        this.data.weekly_price = pivotDataDirty.weekly_price
        this.data.predicted_weekly_occupancy = pivotDataDirty.predicted_weekly_occupancy
      },
      onInput() {
        this.$emit('change', this.isDirty, this.data)
      }
    }
  }
</script>
